import type {ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode} from 'react';
import InlineNotice from './InlineNotice.jsx';
import type {Notice} from './InlineNotice.jsx';
import {RecordBackLink, RecordPageBody, RecordPageFrame} from './RecordPageShell.jsx';
import {recordSecondaryButton} from './recordPageControls.jsx';
import Header from './Header.tsx';

// Extra attributes forwarded to the retry <button>. Every call site that uses
// it passes a data-* hook, and a plain ButtonHTMLAttributes would reject those
// keys in an object literal (TypeScript only waives the check for hyphenated
// names in JSX ATTRIBUTE position, not inside an object), so the data-* index
// signature is what keeps the five daily pages callable from TypeScript.
type RetryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  [dataAttribute: `data-${string}`]: string;
};

// Whatever is not consumed below is spread onto RecordPageBody, which forwards
// it to its root <div> — that is how pages attach their load-error data
// attribute. Mirrors RecordPageShell's own passthrough, `style` included: this
// component never sets one on the body, so it stays available to callers.
type RecordPageLoadErrorProps = HTMLAttributes<HTMLDivElement> & {
  backLabel?: ReactNode;
  onBack?: () => void;
  notice?: Notice | null;
  onRetry?: () => void;
  maxWidth?: CSSProperties['maxWidth'];
  retryLabel?: ReactNode;
  retryButtonProps?: RetryButtonProps;
};

// Standard fail-closed load-error state for record pages. Pages still own the
// loadError value and retry function; this only centralizes the repeated
// frame/body/back/notice/retry chrome.
export default function RecordPageLoadError({
  backLabel,
  onBack,
  notice,
  onRetry,
  maxWidth = 800,
  retryLabel = 'Retry',
  retryButtonProps = {},
  ...bodyProps
}: RecordPageLoadErrorProps) {
  const {style: retryButtonStyle, ...restRetryButtonProps} = retryButtonProps;
  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={maxWidth} {...bodyProps}>
        <RecordBackLink label={backLabel} onBack={onBack} />
        <InlineNotice notice={notice} />
        <button
          type="button"
          {...restRetryButtonProps}
          onClick={onRetry}
          style={{...recordSecondaryButton, marginTop: 10, ...retryButtonStyle}}
        >
          {retryLabel}
        </button>
      </RecordPageBody>
    </RecordPageFrame>
  );
}
